import {ChevronRightIcon, MinusIcon, PlusIcon} from '@heroicons/react/solid';
import {useState} from 'react';

import {Badge, Button, Table, TBody, Td, Th, Thead, Tr} from '@/components';
import type {AfriexDispute} from '@/types/AfriexDispute';
import {AfriexDisputeStatus} from '@/types/AfriexDispute';
import {statusColors} from '@/types/Transaction';
import {formatValues} from '@/utils/dashboard';
import * as formatDate from '@/utils/date';

import DisputeSummary from './DisputeSummary';

export interface DisputeTableProps {
  items: AfriexDispute[];
  handleUpdateDisputeModal: (disputeId: string) => void;
}

const FAILED_DISPUTE_STATUSES = [
  AfriexDisputeStatus.Closed,
  AfriexDisputeStatus.Fraud,
];

// Maps a dispute status onto the badge vocabulary `statusColors` understands.
// Anything unrecognised falls through as-is.
const toBadgeStatus = (status: AfriexDispute['status']) => {
  if (FAILED_DISPUTE_STATUSES.includes(status)) {
    return 'failed';
  }
  if (status === AfriexDisputeStatus.Legit) {
    return 'successful';
  }
  return status;
};

const DisputeTable = ({items, handleUpdateDisputeModal}: DisputeTableProps) => {
  return (
    <Table>
      <Thead>
        <Tr enableHover={false}>
          <Th className="w-20">&nbsp;</Th>
          <Th>Amount</Th>
          <Th className="px-5">Currency</Th>
          <Th className="w-auto">Status</Th>
          <Th className="px-5">Reason</Th>
          <Th>Comments</Th>
          <Th className="px-5">Created</Th>
          <Th className="w-20">&nbsp;</Th>
        </Tr>
      </Thead>
      <TBody>
        {items.map((item, index) => (
          <DisputeRow
            key={index}
            item={item}
            handleUpdateDisputeModal={handleUpdateDisputeModal}
            index={index}
          />
        ))}
      </TBody>
    </Table>
  );
};

const DisputeRow = ({
  item,
  handleUpdateDisputeModal,
  index,
}: {
  item: AfriexDispute;
  handleUpdateDisputeModal: DisputeTableProps['handleUpdateDisputeModal'];
  index: number;
}) => {
  const [isExpanded, setIsExpanded] = useState(false);

  const disputeStatus = toBadgeStatus(item.status);

  const zebraRow = index % 2 === 0 ? '' : 'bg-gray-50 dark:bg-dark-zebraRow';

  return (
    <>
      <Tr className={zebraRow}>
        <Td isNumeric className="px-5">
          <Button onClick={() => setIsExpanded(!isExpanded)} variant="unStyled">
            <div
              className={`border-2 border-gray-700 dark:border-dark-borderLight dark:text-[#FCFBF5] text-gray-700 w-5 h-5 flex justify-center rounded hover:bg-cyan-50 ${
                isExpanded && 'bg-cyan-50'
              }`}
            >
              {isExpanded ? (
                <MinusIcon className="w-4 h-4" />
              ) : (
                <PlusIcon className="w-4 h-4" />
              )}
            </div>
          </Button>
        </Td>

        <Td className={`font-medium`}>{formatValues(item.amount, 'money')}</Td>
        <Td className="px-5">{item.currency}</Td>
        <Td>
          <Badge
            label={item.status}
            colorScheme={statusColors[disputeStatus]}
          />
        </Td>
        <Td className="px-5">
          <p className="truncate w-20" title={item.reason}>
            {item.reason}
          </p>
        </Td>
        <Td>
          <p className="truncate w-20" title={item.userNote}>
            {item.userNote || '-'}
          </p>
        </Td>
        <Td className="px-5">
          {formatDate.DDMonYYYY(String(item.createdAt || item.updatedAt), true)}
        </Td>
        <Td>
          <div className="w-5 h-5 flex justify-center text-gray-700 dark:text-gray-300 hover:border rounded-lg hover:border-gray-300 hover:bg-cyan-50 hover:text-cyan-500">
            <ChevronRightIcon className="w-5 h-5" />
          </div>
        </Td>
      </Tr>

      {isExpanded && (
        <Tr enableHover={false}>
          <Td colSpan={8} className="p-0">
            <div className="flex py-10 px-8 justify-center bg-sky-50 dark:bg-transparent w-full">
              <DisputeSummary
                data={item}
                handleUpdateDisputeModal={handleUpdateDisputeModal}
              />
            </div>
          </Td>
        </Tr>
      )}
    </>
  );
};

export default DisputeTable;
